/**
 * Hakro cross-shop cart sync
 * ----------------------------------------------------------------------
 * Copy this file into the other shop's theme as well (D2C and B2B).
 * Both themes must:
 *   - load this script
 *   - set window.HakroCartSyncConfig with a *different* `shop` key
 *     ("d2c" on shop.hakro.com, "b2b" on hakro.com)
 *   - use the same cookie name and cookie domain
 *
 * Live *.hakro.com: last cart is stored in a Domain=.hakro.com cookie and
 * applied on any later visit to the other shop. No URL hash.
 *
 * Match key: SKU (barcode fallback). Variant IDs are never copied.
 */
(function (global) {
  var APPLIED_TS_KEY = 'hakro_cart_applied_ts';
  var APPLYING_KEY = 'hakro_cart_applying';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 2;
  var COOKIE_MAX_BYTES = 3500;
  var MAX_LINE_QTY = 999;
  var MAX_PROP_LENGTH = 100;
  var API_VERSION = '2024-10';

  /*
    Only these line-item properties travel in the cross-shop cookie. Keys
    starting with "_" are technical (hidden) properties. Anything else, in
    particular the gift-card recipient fields (name, email, message), is
    personal data and must not be written to a Domain=.hakro.com cookie.
  */
  var DEFAULT_PROPERTY_ALLOW_LIST = ['Size', 'Modellnummer', 'Color code'];

  var defaults = {
    enabled: true,
    shop: 'd2c',
    cookieDomain: '.hakro.com',
    cookieName: 'hakro_cart',
    token: '',
    apiVersion: API_VERSION,
    // What to do when the other shop's cart arrives and this cart is not empty:
    // 'skip' (keep the current cart), 'confirm' (ask), 'replace' (old behaviour).
    nonEmptyCart: 'skip',
    propertyAllowList: DEFAULT_PROPERTY_ALLOW_LIST,
  };

  function config() {
    var incoming = global.HakroCartSyncConfig || {};
    return {
      enabled: incoming.enabled !== false,
      shop: incoming.shop || defaults.shop,
      cookieDomain: incoming.cookieDomain || defaults.cookieDomain,
      cookieName: incoming.cookieName || defaults.cookieName,
      token: incoming.token || '',
      apiVersion: incoming.apiVersion || defaults.apiVersion,
      nonEmptyCart: incoming.nonEmptyCart || defaults.nonEmptyCart,
      propertyAllowList: Array.isArray(incoming.propertyAllowList)
        ? incoming.propertyAllowList
        : defaults.propertyAllowList,
    };
  }

  function isAllowedProperty(key) {
    if (typeof key !== 'string' || !key) return false;
    if (key.charAt(0) === '_') return true;
    return config().propertyAllowList.indexOf(key) !== -1;
  }

  function clampQuantity(value) {
    var qty = parseInt(value, 10);
    if (!isFinite(qty) || qty < 1) return 1;
    return Math.min(qty, MAX_LINE_QTY);
  }

  function isDesignMode() {
    return Boolean(global.Shopify && Shopify.designMode);
  }

  function routes() {
    var r = (global.FoxTheme && FoxTheme.routes) || {};
    var cart = r.cart_url || '/cart';
    if (cart.slice(-3) === '.js') cart = cart.slice(0, -3);
    return {
      cartJs: cart + '.js',
      cartClear: cart + '/clear.js',
      cartAdd: r.cart_add_url || cart + '/add.js',
    };
  }

  function jsonFetch(url, options) {
    return fetch(url, options).then(function (res) {
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function getCart() {
    return jsonFetch(routes().cartJs, { headers: { Accept: 'application/json' } });
  }

  function serializeCart(cart) {
    var cfg = config();
    var lines = (cart && cart.items) || [];
    var items = [];

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i] || {};
      var sku = line.sku ? String(line.sku) : '';
      var barcode = line.barcode ? String(line.barcode) : '';
      if (!sku && !barcode) continue;

      var props = {};
      var src = line.properties || {};
      Object.keys(src).forEach(function (key) {
        if (!isAllowedProperty(key)) return;
        if (src[key] == null || src[key] === '' || typeof src[key] === 'object') return;
        props[key] = String(src[key]).slice(0, MAX_PROP_LENGTH);
      });

      items.push({
        sku: sku,
        barcode: barcode,
        handle: line.handle ? String(line.handle) : '',
        qty: clampQuantity(line.quantity),
        props: props,
      });
    }

    if (lines.length > 0 && items.length === 0) {
      console.warn('HakroCartSync: cart has items but none have a SKU/barcode; not writing.');
      return null;
    }

    return {
      v: 1,
      ts: Date.now(),
      shop: cfg.shop,
      items: items,
    };
  }

  function cookieDomainForHost() {
    var configured = config().cookieDomain || '';
    if (!configured) return '';
    var host = location.hostname;
    var suffix = configured.replace(/^\./, '');
    if (host === suffix || host.slice(-1 * (suffix.length + 1)) === '.' + suffix) {
      return configured.charAt(0) === '.' ? configured : '.' + configured;
    }
    return '';
  }

  function readCookie() {
    var name = config().cookieName;
    var cookies = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < cookies.length; i++) {
      var eq = cookies[i].indexOf('=');
      if (eq < 0) continue;
      if (cookies[i].slice(0, eq) !== name) continue;
      try {
        return JSON.parse(decodeURIComponent(cookies[i].slice(eq + 1)));
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  function writeCookie(payload) {
    if (!payload) return false;
    var name = config().cookieName;
    var fit = payload;
    var encoded = encodeURIComponent(JSON.stringify(fit));

    while (encoded.length > COOKIE_MAX_BYTES && fit.items && fit.items.length > 0) {
      fit = {
        v: fit.v,
        ts: fit.ts,
        shop: fit.shop,
        items: fit.items.slice(0, -1),
      };
      encoded = encodeURIComponent(JSON.stringify(fit));
    }

    if (encoded.length > COOKIE_MAX_BYTES) {
      console.warn('HakroCartSync: cart cookie still too large after truncating items.');
      return false;
    }

    if (fit.items && payload.items && fit.items.length < payload.items.length) {
      console.warn(
        'HakroCartSync: truncated cart cookie from ' + payload.items.length + ' to ' + fit.items.length + ' lines.'
      );
    }

    var parts = [
      name + '=' + encoded,
      'Max-Age=' + COOKIE_MAX_AGE,
      'Path=/',
      'SameSite=Lax',
    ];
    var domain = cookieDomainForHost();
    if (domain) parts.push('Domain=' + domain);
    if (location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
    return true;
  }

  function lastAppliedTs() {
    try {
      return parseInt(sessionStorage.getItem(APPLIED_TS_KEY) || '0', 10) || 0;
    } catch (e) {
      return 0;
    }
  }

  function setLastAppliedTs(ts) {
    try {
      sessionStorage.setItem(APPLIED_TS_KEY, String(ts || 0));
    } catch (e) {}
  }

  function isApplying() {
    try {
      return sessionStorage.getItem(APPLYING_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setApplying(on) {
    try {
      if (on) sessionStorage.setItem(APPLYING_KEY, '1');
      else sessionStorage.removeItem(APPLYING_KEY);
    } catch (e) {}
  }

  function publishCart(cart) {
    if (global.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
    }
    document.dispatchEvent(new CustomEvent('hakro-cart-sync:applied', { detail: { cart: cart } }));
  }

  var handleCache = {};

  function productByHandle(handle) {
    if (!handle) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(handleCache, handle)) {
      return Promise.resolve(handleCache[handle]);
    }
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (product) {
        handleCache[handle] = product || null;
        return handleCache[handle];
      })
      .catch(function () {
        handleCache[handle] = null;
        return null;
      });
  }

  function variantFromProduct(product, line) {
    if (!product || !product.variants) return null;
    var sku = String(line.sku || '');
    var barcode = String(line.barcode || '');
    for (var i = 0; i < product.variants.length; i++) {
      var variant = product.variants[i];
      if (sku && variant.sku && String(variant.sku) === sku) return variant;
      if (barcode && variant.barcode && String(variant.barcode) === barcode) return variant;
    }
    return null;
  }

  function numericVariantId(value) {
    if (value == null) return null;
    if (typeof value === 'number') return value;
    var text = String(value);
    var match = text.match(/(\d+)\s*$/);
    return match ? Number(match[1]) : null;
  }

  function storefrontLookup(line) {
    var cfg = config();
    if (!cfg.token) return Promise.resolve(null);

    var clauses = [];
    if (line.sku) clauses.push('sku:' + JSON.stringify(String(line.sku)));
    if (line.barcode) clauses.push('barcode:' + JSON.stringify(String(line.barcode)));
    if (!clauses.length) return Promise.resolve(null);

    var query =
      'query CartSyncVariant($q: String!) { productVariants(first: 1, query: $q) { edges { node { id sku barcode } } } }';

    return fetch('/api/' + cfg.apiVersion + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token,
      },
      body: JSON.stringify({ query: query, variables: { q: clauses.join(' OR ') } }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        var edges = json && json.data && json.data.productVariants && json.data.productVariants.edges;
        var node = edges && edges[0] && edges[0].node;
        return numericVariantId(node && node.id);
      })
      .catch(function () {
        return null;
      });
  }

  function resolveLine(line) {
    return productByHandle(line.handle).then(function (product) {
      var variant = variantFromProduct(product, line);
      var id = variant ? numericVariantId(variant.id) : null;
      if (id) return id;
      return storefrontLookup(line);
    });
  }

  function resolveItems(payload) {
    var lines = (payload && payload.items) || [];
    var pending = lines.map(function (line) {
      return resolveLine(line).then(function (id) {
        if (!id) {
          console.warn('HakroCartSync: could not map SKU', line.sku || line.barcode);
          return null;
        }
        var item = { id: id, quantity: clampQuantity(line.qty) };
        var props = {};
        var src = line.props && typeof line.props === 'object' ? line.props : {};
        Object.keys(src).forEach(function (key) {
          if (!isAllowedProperty(key) || typeof src[key] !== 'string') return;
          props[key] = src[key].slice(0, MAX_PROP_LENGTH);
        });
        if (Object.keys(props).length) item.properties = props;
        return item;
      });
    });
    return Promise.all(pending).then(function (mapped) {
      return mapped.filter(Boolean);
    });
  }

  function clearCart() {
    return fetch(routes().cartClear, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    }).then(function (res) {
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function addItems(items) {
    if (!items.length) return Promise.resolve({});
    return fetch(routes().cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: items }),
    }).then(function (res) {
      return res.json();
    });
  }

  function shouldApply(payload) {
    if (!payload || typeof payload !== 'object') return false;
    if (payload.v !== 1 || !Array.isArray(payload.items)) return false;
    if (payload.shop && payload.shop === config().shop) return false;
    var ts = parseInt(payload.ts, 10) || 0;
    return ts > lastAppliedTs();
  }

  /*
    The cookie is writable by every *.hakro.com host, so the current cart is
    only replaced when it is empty (default), or after the shopper agreed
    (nonEmptyCart: 'confirm'), or when the theme explicitly opts in
    ('replace').
  */
  function mayReplaceCart(cart, payload) {
    var hasItems = cart && Array.isArray(cart.items) && cart.items.length > 0;
    if (!hasItems) return true;
    var mode = config().nonEmptyCart;
    if (mode === 'replace') return true;
    if (mode === 'confirm') {
      var count = (payload.items || []).length;
      var message = global.HakroCartSyncConfig && global.HakroCartSyncConfig.confirmMessage;
      if (!message) {
        message =
          'Replace the ' + cart.items.length + ' item(s) in this cart with the ' + count + ' item(s) from your other HAKRO cart?';
      }
      return global.confirm(message);
    }
    return false;
  }

  function applyPayload(payload) {
    if (!shouldApply(payload)) return Promise.resolve(false);
    if (isApplying()) return Promise.resolve(false);

    setApplying(true);
    handleCache = {};

    return getCart()
      .then(function (cart) {
        if (!mayReplaceCart(cart, payload)) {
          // Remember this payload so the question is not asked again on every page.
          setLastAppliedTs(payload.ts);
          return null;
        }
        return resolveItems(payload)
          .then(function (items) {
            return clearCart().then(function () {
              return addItems(items);
            });
          })
          .then(getCart)
          .then(function (updated) {
            setLastAppliedTs(payload.ts);
            publishCart(updated);
            return true;
          });
      })
      .then(function (result) {
        return result === true;
      })
      .catch(function (err) {
        console.error('HakroCartSync: apply failed', err);
        return false;
      })
      .then(function (ok) {
        setApplying(false);
        return ok;
      });
  }

  function persist(cart) {
    if (!config().enabled || isDesignMode() || isApplying()) return Promise.resolve(null);

    var run = cart && Array.isArray(cart.items) && !cart.errors ? Promise.resolve(cart) : getCart();
    return run.then(function (json) {
      if (!json || json.errors || json.status) return null;
      if (!Array.isArray(json.items)) return null;
      var payload = serializeCart(json);
      if (!payload) return null;
      writeCookie(payload);
      return payload;
    });
  }

  function navigateWithCart(url) {
    return persist().then(function () {
      location.href = url;
    });
  }

  function subscribeCartUpdates() {
    if (!global.FoxTheme || !FoxTheme.pubsub || !FoxTheme.pubsub.PUB_SUB_EVENTS) return false;
    if (subscribeCartUpdates.wired) return true;
    subscribeCartUpdates.wired = true;
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (data) {
      persist(data && data.cart);
    });
    return true;
  }

  function init() {
    if (!config().enabled || isDesignMode()) return;

    subscribeCartUpdates();
    if (!subscribeCartUpdates.wired) {
      document.addEventListener('page:loaded', subscribeCartUpdates);
      setTimeout(subscribeCartUpdates, 0);
    }

    var fromCookie = readCookie();
    if (fromCookie && shouldApply(fromCookie)) {
      applyPayload(fromCookie);
      return;
    }

    if (!fromCookie) {
      getCart().then(function (cart) {
        if (cart && cart.items && cart.items.length) persist(cart);
      });
    }
  }

  global.HakroCartSync = {
    persist: persist,
    navigateWithCart: navigateWithCart,
    applyPayload: applyPayload,
    serializeCart: serializeCart,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window);
